"use client"

/**
 * Fertilizer Schedule Calculator — interactive island.
 *
 * Ported from the "Fertilizer Schedule Calculator" Claude Design prototype
 * (imported 2026-07-07). The visual system (green gradient cards, rounded
 * chips, calendar strip, next-application callout) matches the design; the
 * logic is wired to the real engine in lib/fertilizer-product-calculator and
 * the real ZIP→region lookup.
 *
 * Styling is deliberately inline (matching the design source) rather than
 * Tailwind utility classes: the site CSS is a precompiled string
 * (lib/generated/tailwind.ts) that doesn't pick up new arbitrary classes in
 * dev until compile-tailwind runs, so inline styles render correctly
 * everywhere without that build step.
 *
 * Prefill/deep-links use useSearchParams, so this component MUST render inside a
 * Suspense boundary — the exported `FertilizerScheduleCalculator` provides it.
 */

import { Suspense, useEffect, useMemo, useRef, useState } from "react"
import { SearchParamsHydrationGate } from "@/components/ssr/search-params-hydration-gate"
import { ZIP_INPUT_ATTRS, sanitizeZip } from "@/lib/zip-input"
import { usePrerenderSafeSearchParams } from "@/hooks/use-prerender-safe-search-params"
import {
  CalendarDays,
  MapPin,
  CalendarClock,
  Scale,
  Sprout,
  AlertTriangle,
  ArrowRight,
  BookOpen,
} from "lucide-react"
import { calculateFertilizerSchedule } from "@/lib/fertilizer-product-calculator/calculator"
import { FERTILIZER_PRODUCTS, FERTILIZER_GRASS_OPTIONS, getGrassOption } from "@/data/fertilizer-products"
import type { FertilizerProduct } from "@/data/fertilizer-products"
import { getProductByKey } from "@/data/guide-product-recommendations"
import { guideSlugToGrassName } from "@/lib/grass-slug"
import { analytics, contentAnalytics, stashEmailCaptureAttribution } from "@/lib/analytics"
import { ResultEmailCaptureCard, ToolNextStepCard } from "@/components/tools/shared"
import { ProductGuideCta } from "@/components/lead-magnets/product-guide-cta"
import { useCalculatorStarted } from "@/hooks/useCalculatorStarted"
import { useAffiliateImpression } from "@/hooks/use-affiliate-impression"
import { useFromTool } from "@/hooks/use-from-tool"

const GREEN = "#16a34a"
const GREEN_DARK = "#15803d"
const GREEN_DEEP = "#14532d"
const GREEN_600_700 = "linear-gradient(90deg, #16a34a 0%, #059669 100%)"
import { amazonSearchUrl } from "@/lib/affiliate"

function affiliateFor(product: FertilizerProduct): { url: string; image?: string; price?: string } {
  if (product.productKey) {
    const rec = getProductByKey(product.productKey)
    if (rec) return { url: rec.affiliateUrl, image: rec.imageUrl, price: rec.price }
  }
  return {
    url: amazonSearchUrl(product.name),
  }
}

const SIZE_PRESETS = [2500, 5000, 10000, 20000]

interface CalculatorInnerProps {
  /** When set, the product picker is hidden and this product is locked in. */
  lockedProductId?: string
  /** Initial product when nothing is in the URL and no product is locked. */
  defaultProductId?: string
}

function CalculatorInner({ lockedProductId, defaultProductId = "milorganite" }: CalculatorInnerProps) {
  const searchParams = usePrerenderSafeSearchParams()
  // calculator_started on first input — calculator_used is gated on a 5-digit
  // ZIP, so it can't measure pre-ZIP abandonment.
  const startedRef = useCalculatorStarted('fertilizer-schedule')

  const initialProduct =
    lockedProductId ??
    (searchParams.get("product") && FERTILIZER_PRODUCTS.some((p) => p.id === searchParams.get("product"))
      ? (searchParams.get("product") as string)
      : defaultProductId)
  const initialZip = (() => {
    const z = searchParams.get("zip")
    return z && /^\d{5}$/.test(z) ? z : ""
  })()
  const initialArea = (() => {
    const s = searchParams.get("size")
    const n = s ? parseInt(s, 10) : NaN
    return !isNaN(n) && n > 0 ? n : 5000
  })()

  // `?grass=` rides in on cross-tool links (buildToolUrl emits a guide slug for
  // this tool). Honoring it doubles as the provenance signal below: a prefilled
  // grass is a grass the user picked somewhere, not our silent default.
  const initialGrassId = (() => {
    const canonical = guideSlugToGrassName(searchParams.get("grass"))
    if (!canonical) return null
    return FERTILIZER_GRASS_OPTIONS.find((g) => g.canonical === canonical)?.id ?? null
  })()

  const [productId, setProductId] = useState(initialProduct)
  const [zip, setZip] = useState(initialZip)
  const [area, setArea] = useState(initialArea)
  const [grassId, setGrassId] = useState(initialGrassId ?? "kbg")
  // Provenance for the grass type. The default "kbg" is indistinguishable from a
  // user who deliberately chose Kentucky Bluegrass, so track the difference
  // explicitly: everything downstream that wants to know "do we actually know
  // this lawn's grass?" must read this, not the value.
  const [grassTypeSource, setGrassTypeSource] = useState<"user" | "default">(
    initialGrassId ? "user" : "default",
  )

  const effectiveProductId = lockedProductId ?? productId

  // Inbound cross-tool attribution. This family accepts `?grass=` prefills from
  // buildToolUrl links, so report the referred visit under the route the user
  // actually landed on: the locked product's own slug, or the hub calculator.
  const routeToolId = lockedProductId
    ? (FERTILIZER_PRODUCTS.find((p) => p.id === lockedProductId)?.slug ?? "fertilizer-schedule-calculator")
    : "fertilizer-schedule-calculator"
  useFromTool(routeToolId)

  // In-result "Check price" CTA telemetry. Surface carries the product id so the
  // ~10 branded routes stay distinguishable in one CTR query.
  const ctaProduct = FERTILIZER_PRODUCTS.find((p) => p.id === effectiveProductId)
  const ctaAffiliate = ctaProduct ? affiliateFor(ctaProduct) : undefined
  const ctaSurface = `tool-fertilizer-schedule-cta:${effectiveProductId}`
  const ctaImpressionRef = useAffiliateImpression(ctaAffiliate?.url ? ctaSurface : undefined, {
    article_slug: `tool:${routeToolId}`,
    product_name: ctaProduct?.name ?? "",
    affiliate_link: ctaAffiliate?.url ?? "",
    source_context: effectiveProductId,
  })

  const result = useMemo(
    () => calculateFertilizerSchedule({ productId: effectiveProductId, zipCode: zip, areaSqFt: area, grassId }),
    [effectiveProductId, zip, area, grassId],
  )

  const trackedRef = useRef(false)
  useEffect(() => {
    if (!result || zip.length !== 5 || trackedRef.current) return
    trackedRef.current = true
    analytics.calculatorUsed("fertilizer-schedule", {
      inputs: { product: effectiveProductId, grass_type: grassId, lawn_size_sqft: area, zip },
      result: {
        per_app_amount: result.perApp.amount,
        containers: result.perApp.containers,
        apps_per_year: result.appsPerYear,
      },
      source: "live_update",
    })
  }, [result, zip, effectiveProductId, grassId, area])

  if (!result) return null
  const { product } = result
  const aff = affiliateFor(product)
  const assumedGrassLabel = getGrassOption(grassId)?.label ?? "Kentucky Bluegrass"

  return (
    <div ref={startedRef} style={{ color: "#111827" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(330px, 1fr))",
          gap: 28,
          alignItems: "start",
        }}
      >
        {/* ============ FORM ============ */}
        <div
          style={{
            borderRadius: 24,
            background: "#fff",
            border: "1px solid #e5e7eb",
            padding: 28,
            boxShadow: "0 12px 40px rgba(0,0,0,0.04)",
          }}
        >
          {!lockedProductId && (
            <>
              <SectionLabel>1 · Your product</SectionLabel>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
                  gap: 10,
                }}
              >
                {FERTILIZER_PRODUCTS.map((p) => {
                  const selected = p.id === productId
                  const pAff = affiliateFor(p)
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setProductId(p.id)}
                      aria-pressed={selected}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                        boxSizing: "border-box",
                        padding: 10,
                        border: selected ? `2px solid ${GREEN}` : "1px solid #e5e7eb",
                        borderRadius: 16,
                        cursor: "pointer",
                        fontFamily: "inherit",
                        textAlign: "left",
                        background: selected ? "#f0fdf4" : "#fff",
                      }}
                    >
                      <div
                        style={{
                          width: "100%",
                          height: 80,
                          borderRadius: 10,
                          overflow: "hidden",
                          background: "#f9fafb",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {pAff.image ? (
                          <img
                            src={pAff.image}
                            alt={p.name}
                            style={{ width: "100%", height: "100%", objectFit: "contain" }}
                          />
                        ) : (
                          <Sprout style={{ width: 26, height: 26, color: GREEN }} aria-hidden="true" />
                        )}
                      </div>
                      <span style={{ fontSize: 12.5, fontWeight: 700, lineHeight: 1.3, color: "#111827" }}>
                        {p.name}
                      </span>
                      <span
                        style={{
                          display: "inline-flex",
                          alignSelf: "flex-start",
                          padding: "2px 8px",
                          borderRadius: 9999,
                          fontSize: 10.5,
                          fontWeight: 700,
                          letterSpacing: "0.03em",
                          textTransform: "uppercase",
                          color: selected ? "#166534" : "#6b7280",
                          background: selected ? "#dcfce7" : "#f3f4f6",
                        }}
                      >
                        {p.tag}
                      </span>
                    </button>
                  )
                })}
              </div>
            </>
          )}

          <SectionLabel style={{ marginTop: lockedProductId ? 0 : 24 }}>
            {lockedProductId ? "1 · Your ZIP code" : "2 · Your ZIP code"}
          </SectionLabel>
          <div style={{ position: "relative" }}>
            <MapPin
              style={{
                position: "absolute",
                top: "50%",
                left: 16,
                transform: "translateY(-50%)",
                width: 18,
                height: 18,
                color: "#9ca3af",
              }}
              aria-hidden="true"
            />
            <input
              {...ZIP_INPUT_ATTRS}
              value={zip}
              onChange={(e) => setZip(sanitizeZip(e.target.value))}
              placeholder="e.g. 44107"
              aria-label="ZIP code"
              style={{
                width: "100%",
                boxSizing: "border-box",
                minHeight: 52,
                padding: "0 130px 0 44px",
                fontFamily: "inherit",
                fontSize: 17,
                fontWeight: 700,
                letterSpacing: "0.04em",
                color: "#111827",
                border: "1px solid #e5e7eb",
                borderRadius: 12,
                background: "#fff",
                outline: "none",
              }}
            />
            <span
              style={{
                position: "absolute",
                top: "50%",
                right: 14,
                transform: "translateY(-50%)",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "4px 12px",
                borderRadius: 9999,
                fontSize: 12,
                fontWeight: 700,
                color: result.regionKnown ? "#166534" : "#6b7280",
                background: result.regionKnown ? "#f0fdf4" : "#f9fafb",
                border: `1px solid ${result.regionKnown ? "#bbf7d0" : "#e5e7eb"}`,
              }}
            >
              {result.region ?? "Enter ZIP"}
            </span>
          </div>

          <SectionLabel style={{ marginTop: 24 }}>{lockedProductId ? "2 · Lawn size" : "3 · Lawn size"}</SectionLabel>
          <div style={{ position: "relative" }}>
            <input
              type="number"
              value={area}
              onChange={(e) => {
                const v = parseFloat(e.target.value)
                setArea(isNaN(v) ? 0 : Math.max(0, v))
              }}
              aria-label="Lawn size in square feet"
              style={{
                width: "100%",
                boxSizing: "border-box",
                minHeight: 52,
                padding: "0 74px 0 16px",
                fontFamily: "inherit",
                fontSize: 17,
                fontWeight: 700,
                color: "#111827",
                border: "1px solid #e5e7eb",
                borderRadius: 12,
                background: "#fff",
                outline: "none",
              }}
            />
            <span
              style={{
                position: "absolute",
                top: "50%",
                right: 16,
                transform: "translateY(-50%)",
                fontSize: 13,
                fontWeight: 600,
                color: "#6b7280",
              }}
            >
              sq ft
            </span>
          </div>
          <input
            type="range"
            min={2.3}
            max={4.48}
            step={0.01}
            value={Math.log10(Math.max(200, area))}
            onChange={(e) => setArea(Math.round(Math.pow(10, parseFloat(e.target.value)) / 100) * 100)}
            aria-label="Lawn size slider"
            style={{ width: "100%", marginTop: 12, accentColor: GREEN }}
          />
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
            {SIZE_PRESETS.map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setArea(v)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  boxSizing: "border-box",
                  minHeight: 36,
                  padding: "0 14px",
                  border: v === area ? `2px solid ${GREEN}` : "1px solid #e5e7eb",
                  borderRadius: 9999,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#111827",
                  background: v === area ? "#f0fdf4" : "#fff",
                }}
              >
                {v.toLocaleString()} sq ft
              </button>
            ))}
          </div>

          <SectionLabel style={{ marginTop: 24 }}>{lockedProductId ? "3 · Grass type" : "4 · Grass type"}</SectionLabel>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
            {FERTILIZER_GRASS_OPTIONS.map((g) => {
              const sel = g.id === grassId
              return (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => {
                    setGrassId(g.id)
                    setGrassTypeSource("user")
                  }}
                  aria-pressed={sel}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    boxSizing: "border-box",
                    minHeight: 40,
                    padding: "0 16px",
                    border: sel ? `2px solid ${GREEN}` : "1px solid #e5e7eb",
                    borderRadius: 9999,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    fontSize: 14,
                    fontWeight: 700,
                    color: sel ? "#166534" : "#374151",
                    background: sel ? "#f0fdf4" : "#fff",
                  }}
                >
                  {g.label}
                </button>
              )
            })}
          </div>
        </div>

        {/* ============ RESULTS RAIL ============ */}
        <div style={{ position: "sticky", top: 24, display: "flex", flexDirection: "column", gap: 16 }}>
          {/* next application callout */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
              borderRadius: 20,
              padding: "18px 22px",
              background: GREEN_600_700,
              color: "#fff",
              boxShadow: "0 8px 20px -6px rgba(22,163,74,0.30)",
            }}
          >
            <CalendarClock style={{ flexShrink: 0, width: 26, height: 26 }} aria-hidden="true" />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", opacity: 0.85 }}>
                Next application
              </div>
              <div style={{ marginTop: 3, fontSize: 22, fontWeight: 800, lineHeight: 1.15, letterSpacing: "-0.01em" }}>
                {product.starterOnly ? "At seeding time" : result.nextApplication?.window ?? "Enter your ZIP"}
              </div>
              <div style={{ marginTop: 3, fontSize: 13, fontWeight: 600, opacity: 0.9 }}>
                {product.starterOnly ? "One application when you plant" : result.nextApplication?.note ?? ""}
              </div>
            </div>
          </div>

          {/* main result card */}
          <div
            style={{
              borderRadius: 24,
              overflow: "hidden",
              background: "#fff",
              border: "1px solid #dcfce7",
              boxShadow: "0 12px 40px rgba(0,0,0,0.06)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "16px 24px",
                background: "#f0fdf4",
                borderBottom: "1px solid #dcfce7",
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 15, fontWeight: 800, color: GREEN_DEEP }}>
                <Scale style={{ width: 18, height: 18, color: GREEN }} aria-hidden="true" />
                Each application
              </span>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#166534" }}>{product.short}</span>
            </div>
            <div style={{ padding: 24 }}>
              <div style={{ padding: 22, borderRadius: 18, background: "#f0fdf4", border: "1px solid #bbf7d0", textAlign: "center" }}>
                <div style={{ fontSize: 44, fontWeight: 800, lineHeight: 1, letterSpacing: "-0.02em", color: GREEN_DEEP }}>
                  {result.perApp.amount} {result.perApp.unit}
                </div>
                <div style={{ marginTop: 8, fontSize: 14, fontWeight: 600, color: "#166534" }}>
                  per application for your lawn
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginTop: 12 }}>
                <StatTile value={`${result.perApp.containers} ${result.perApp.containerLabel}`} label="per application" />
                <StatTile value={result.perApp.rateLabel} label="per 1,000 sq ft" />
                <StatTile value={`${result.perApp.nPer1000Label} lb`} label="N per 1,000 sq ft" />
              </div>

              {/* year schedule */}
              <p style={{ margin: "22px 0 10px", fontSize: 13, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: GREEN_DARK }}>
                Your year ·{" "}
                {result.appsPerYear} {result.appsPerYear === 1 ? "application" : "applications"} · {result.annualContainersLabel}
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 4 }}>
                {result.monthCells.map((m) => (
                  <div key={m.monthIndex} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
                    <div
                      style={{
                        width: "100%",
                        height: 34,
                        borderRadius: 8,
                        background: m.isNext ? GREEN : m.hasApp ? "#dcfce7" : "#f9fafb",
                        border: `1px solid ${m.isNext ? GREEN : m.hasApp ? "#bbf7d0" : "#f3f4f6"}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {m.hasApp && (
                        <Sprout style={{ width: 15, height: 15, color: m.isNext ? "#fff" : GREEN }} aria-hidden="true" />
                      )}
                    </div>
                    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.03em", color: m.hasApp ? "#166534" : "#9ca3af" }}>
                      {m.label}
                    </span>
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 14 }}>
                {result.applications.map((ap) => (
                  <div
                    key={ap.n}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "11px 14px",
                      borderRadius: 12,
                      background: ap.isNext ? "#f0fdf4" : "#fff",
                      border: `1px solid ${ap.isNext ? "#bbf7d0" : "#e5e7eb"}`,
                    }}
                  >
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        width: 26,
                        height: 26,
                        borderRadius: 9999,
                        fontSize: 12.5,
                        fontWeight: 800,
                        color: "#166534",
                        background: "#dcfce7",
                      }}
                    >
                      {ap.n}
                    </span>
                    <span style={{ flex: 1, fontSize: 13.5, fontWeight: 700, color: "#111827" }}>{ap.window}</span>
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: "#6b7280" }}>{ap.note}</span>
                  </div>
                ))}
              </div>

              {/* cautions */}
              {result.cautions.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 16 }}>
                  {result.cautions.map((c, i) => (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 10,
                        padding: "12px 14px",
                        borderRadius: 12,
                        background: "#fffbeb",
                        border: "1px solid #fde68a",
                      }}
                    >
                      <AlertTriangle style={{ flexShrink: 0, width: 16, height: 16, marginTop: 1, color: "#d97706" }} aria-hidden="true" />
                      <span style={{ fontSize: 13, lineHeight: 1.5, color: "#92400e" }}>{c}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* affiliate */}
              <div
                ref={ctaImpressionRef}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  marginTop: 20,
                  padding: "14px 16px",
                  borderRadius: 16,
                  background: "#f9fafb",
                  border: "1px solid #e5e7eb",
                }}
              >
                <div style={{ flexShrink: 0, width: 46, height: 46, borderRadius: 10, overflow: "hidden", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {aff.image ? (
                    <img src={aff.image} alt={product.name} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                  ) : (
                    <Sprout style={{ width: 22, height: 22, color: GREEN }} aria-hidden="true" />
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 14, fontWeight: 700, color: "#111827" }}>{product.name}</span>
                  <span style={{ display: "block", marginTop: 2, fontSize: 12.5, color: "#6b7280" }}>
                    The exact product this schedule is built on{aff.price ? ` · ${aff.price}` : ""}
                  </span>
                </div>
                <a
                  href={aff.url}
                  target="_blank"
                  rel="noopener noreferrer nofollow sponsored"
                  data-affiliate="true"
                  onClick={() =>
                    contentAnalytics.affiliateProductClicked(ctaSurface, {
                      productName: product.name,
                      affiliateLink: aff.url,
                      article_slug: `tool:${routeToolId}`,
                      source_context: effectiveProductId,
                    })
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                    minHeight: 44,
                    padding: "0 18px",
                    borderRadius: 9999,
                    fontSize: 13.5,
                    fontWeight: 700,
                    color: "#fff",
                    background: GREEN_600_700,
                    whiteSpace: "nowrap",
                    textDecoration: "none",
                    boxShadow: "0 8px 20px -6px rgba(22,163,74,0.30)",
                  }}
                >
                  Check price
                  <ArrowRight style={{ width: 15, height: 15 }} aria-hidden="true" />
                </a>
              </div>

              {/* methodology strip */}
              <div style={{ display: "flex", alignItems: "flex-start", gap: 9, marginTop: 16 }}>
                <BookOpen style={{ flexShrink: 0, width: 15, height: 15, marginTop: 2, color: "#9ca3af" }} aria-hidden="true" />
                <p style={{ margin: 0, fontSize: 12, lineHeight: 1.55, color: "#6b7280" }}>
                  {product.labelSource} Always follow the bag label.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Tier 3 account-first capture, below the schedule (not competing with the
          in-result affiliate CTA). Product-specific deliverable + generic,
          non-calendar treatment: the "grass" here is a code and the payoff is the
          product schedule, not the 12-month grass calendar. */}
      <div style={{ marginTop: 24 }}>
        <ResultEmailCaptureCard
          source={`tool_result_${product.id}`}
          toolDisplayName={`${product.name} schedule`}
          subcopy="Keep this schedule handy and track your lawn over time."
          zipCode={zip || undefined}
          accountLead={{
            redirectTo:
              typeof window !== "undefined"
                ? window.location.pathname + window.location.search
                : "/tools/fertilizer-schedule-calculator",
            headline: `Save my ${product.name} schedule`,
            subcopy: "Keep this schedule in a free account and track your lawn over time.",
            onBeforeOAuth: () => stashEmailCaptureAttribution(`tool_result_${product.id}`),
          }}
          successSubcopy={`Your lawn tips are on the way. Your ${product.name} schedule stays right here to revisit anytime.`}
        />
      </div>
      {/* Grass-ID handoff. Shows ONLY while the grass type is still our default:
          the whole schedule is silently built on Kentucky Bluegrass, so the
          highest-value next step is confirming the grass, not another tool.
          Once the user picks a grass (or arrives with one prefilled) this
          disappears and nothing replaces it — these routes carry no
          RelatedTools block, so there is never a second competing CTA here. */}
      {grassTypeSource === "default" && (
        <ToolNextStepCard
          toolId={`${product.id}-calculator`}
          assumedGrassLabel={assumedGrassLabel}
          heading={`Is ${assumedGrassLabel} actually your grass?`}
          tagline={`This ${product.name} schedule is built on that assumption. Snap a photo and confirm your grass type in seconds, then the numbers fit your lawn.`}
        />
      )}
      {/* Buyer's-guide lead magnet: one placement here covers all 11
          fertilizer-schedule/product-calculator routes (2026-07-18 placement
          audit — these are the most product-intent tool surfaces on the site). */}
      <ProductGuideCta source={`${product.id}_calculator_result`} variant="result" />
    </div>
  )
}

function SectionLabel({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <p
      style={{
        margin: "0 0 10px",
        fontSize: 13,
        fontWeight: 700,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        color: GREEN_DARK,
        ...style,
      }}
    >
      {children}
    </p>
  )
}

function StatTile({ value, label }: { value: string; label: string }) {
  return (
    <div style={{ padding: "13px 8px", borderRadius: 14, background: "#f9fafb", border: "1px solid #e5e7eb", textAlign: "center" }}>
      <div style={{ fontSize: 18, fontWeight: 800, lineHeight: 1.05, color: "#111827" }}>{value}</div>
      <div style={{ marginTop: 5, fontSize: 11.5, fontWeight: 600, color: "#6b7280" }}>{label}</div>
    </div>
  )
}

export function FertilizerScheduleCalculator(props: CalculatorInnerProps) {
  return (
    <Suspense
      fallback={
        <div
          style={{
            minHeight: 420,
            borderRadius: 24,
            background: "#fff",
            border: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#9ca3af",
          }}
        >
          <CalendarDays style={{ width: 28, height: 28 }} aria-hidden="true" />
        </div>
      }
    >
      <SearchParamsHydrationGate dateSensitive>
        <CalculatorInner {...props} />
      </SearchParamsHydrationGate>
    </Suspense>
  )
}
